/**
 * Outlook-style scope for series cancel / time-edit.
 * Sent to weplace as the `scope` field on series APIs.
 */
export enum SeriesBookingScope {
  /** Only the opened day (selected occurrence). User-facing: "Only this day". */
  OCCURRENCE = "occurrence",
  /** Opened day plus all later active days in the same series. User-facing: "This day and later". */
  THIS_AND_FUTURE = "thisAndFuture",
  /**
   * Remaining active days from today through the end of the series.
   * Days before today are omitted. User-facing: "All days in the series".
   */
  ENTIRE_SERIES = "entireSeries"
}

/**
 * How series time-edit handles availability conflicts after validation.
 * Sent to weplace as the optional `mode` on update-series-booking-times.
 */
/** One day a series scope will cancel or edit. `start` is UTC ISO-8601. */
export type SeriesScopeDay = {
  /** Active booking row in this scope. */
  id: number;
  /** Occurrence start, UTC ISO-8601. The dialog formats this in the booking timezone. */
  start: string;
};

/**
 * Days one series scope will change.
 * Returned inside `GET /booking/series-scope-targets`.
 */
export type SeriesScopeTargetSet = {
  /** How many days this scope will change. Zero keeps Confirm off for that choice. */
  count: number;
  /** Earliest start. Null when the scope has no days. */
  firstStart: string | null;
  /** Latest start. Null when the scope has no days. */
  lastStart: string | null;
  /** Days sorted by start, earliest first. */
  days: SeriesScopeDay[];
};

/**
 * Preview of all three series scopes for the opened booking.
 * The scope dialog lists these dates before cancel or time-edit.
 */
export type SeriesScopeTargetsResponse = {
  /** Only the opened day. */
  occurrence: SeriesScopeTargetSet;
  /** Opened day and every later active day in the series. */
  thisAndFuture: SeriesScopeTargetSet;
  /**
   * Active days from today onward.
   * Days before today are omitted.
   */
  entireSeries: SeriesScopeTargetSet;
};

export enum SeriesBookingEditMode {
  /** Validate all scoped days first; if any day blocks, update nothing and return a conflict payload (default). */
  ALL_OR_NOTHING = "allOrNothing",
  /** After the user chooses "Update the free days", apply new times only to non-blocking days in the original scope. */
  SKIP_CONFLICTS = "skipConflicts"
}
